import Intercom, { boot, shutdown } from "@intercom/messenger-js-sdk"
import { intercomAppId, intercomMessengerEnvironment } from "@/config"
import { isEmbeddedContext } from "@common/services/embed-service"
import * as Sentry from "@sentry/vue"

// Preview environment ID only exists for the production Messenger config; omit the key entirely elsewhere.
// Read lazily (not at module scope) so partial `@/config` mocks in tests don't need to stub it.
function getMessengerEnvironmentConfig() {
  return intercomMessengerEnvironment ? { messenger_environment: intercomMessengerEnvironment } : {}
}

function initializeIntercom() {
  // Embedded sessions must never show or authenticate with Intercom.
  if (isEmbeddedContext()) return

  // Only this initial call may use the default export — it's the one that injects the
  // widget's loader script. Later calls must use boot()/shutdown(), since re-calling this
  // once already loaded just diffs into an update() rather than a real re-identification.
  Intercom({
    app_id: intercomAppId,
    hide_default_launcher: false,
    ...getMessengerEnvironmentConfig()
  })
}

function identifyUser(userId: string, intercomUserJwt: string) {
  // Intercom's own JWT docs warn that switching identity via update() can cause a "JWT
  // identity mismatch" — shutdown() then a fresh boot() is the documented way to do it.
  shutdown()
  boot({
    app_id: intercomAppId,
    hide_default_launcher: false,
    ...getMessengerEnvironmentConfig(),
    user_id: userId,
    intercom_user_jwt: intercomUserJwt
  })
}

function resetToAnonymous() {
  try {
    shutdown()
    boot({
      app_id: intercomAppId,
      hide_default_launcher: false,
      ...getMessengerEnvironmentConfig()
    })
  } catch (error) {
    // Local logout must proceed even if Intercom fails — don't block caller cleanup.
    Sentry.captureException(error)
  }
}

export default { initializeIntercom, identifyUser, resetToAnonymous }
